<header class="ai-assistant-header">
  <div class="ai-assistant-title">
    <span class="ai-assistant-title-icon" aria-hidden="true">
      <i class="pi pi-sparkles"></i>
    </span>
    <span class="ai-assistant-title-text">
      <span class="ai-assistant-title-name">Rob</span>
    </span>
  </div>
  <div class="ai-assistant-header-actions">
    <button
      pButton
      type="button"
      icon="pi pi-plus"
      [text]="true"
      [pTooltip]="'AI_ASSISTANT.NEW_CHAT' | translate"
      [attr.aria-label]="'AI_ASSISTANT.NEW_CHAT' | translate"
      (click)="startNewConversation()"></button>
    <button
      pButton
      type="button"
      icon="pi pi-clock"
      [text]="true"
      [disabled]="chat.isSending()"
      [pTooltip]="'AI_ASSISTANT.HISTORY' | translate"
      tooltipPosition="left"
      [attr.aria-label]="'AI_ASSISTANT.HISTORY' | translate"
      (click)="toggleHistory()"></button>
    <button
      pButton
      type="button"
      icon="pi pi-times"
      [text]="true"
      [pTooltip]="'GENERAL.CLOSE' | translate"
      [attr.aria-label]="'GENERAL.CLOSE' | translate"
      (click)="onClose()"></button>
  </div>
</header>

<div class="ai-assistant-body" #messagesContainer>
  @if (chat.isHistoryOpen()) {
    <section class="ai-assistant-history" aria-live="polite">
      <div class="ai-assistant-history__header">
        <span>{{ 'AI_ASSISTANT.HISTORY' | translate }}</span>
        <button
          pButton
          type="button"
          icon="pi pi-times"
          [text]="true"
          [attr.aria-label]="'GENERAL.CLOSE' | translate"
          (click)="toggleHistory()"></button>
      </div>
      @if (chat.historyFailed()) {
        <div class="ai-assistant-error" role="alert">
          {{ 'AI_ASSISTANT.HISTORY_ERROR' | translate }}
        </div>
      }
      @if (chat.isLoadingHistory()) {
        <div class="ai-assistant-history__loading">
          <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
          <span>{{ 'AI_ASSISTANT.HISTORY_LOADING' | translate }}</span>
        </div>
      } @else if (chat.sessions().length === 0) {
        <div class="ai-assistant-history__empty">
          {{ 'AI_ASSISTANT.HISTORY_EMPTY' | translate }}
        </div>
      } @else {
        <div class="ai-assistant-history__list">
          @for (session of chat.sessions(); track session.id) {
            <div class="ai-assistant-history__item">
              <button
                type="button"
                class="ai-assistant-history__item-open"
                (click)="openSession(session.id)">
                <span class="ai-assistant-history__item-title">{{ session.title }}</span>
                <span class="ai-assistant-history__item-time">
                  {{ formatRelative(session.lastActivityAt) }}
                </span>
              </button>
              <button
                pButton
                type="button"
                class="ai-assistant-history__item-delete"
                icon="pi pi-trash"
                [text]="true"
                [pTooltip]="'AI_ASSISTANT.HISTORY_DELETE' | translate"
                [attr.aria-label]="'AI_ASSISTANT.HISTORY_DELETE' | translate"
                [attr.title]="'AI_ASSISTANT.HISTORY_DELETE' | translate"
                (click)="deleteSession($event, session.id)"></button>
            </div>
          }
        </div>
      }
    </section>
  } @else {
    <div class="ai-assistant-messages" aria-live="polite" [attr.aria-busy]="chat.isSending()">
      @if (!chat.hasMessages()) {
        <article class="ai-assistant-message">
          <div class="ai-assistant-message__avatar" aria-hidden="true">
            <i class="pi pi-sparkles"></i>
          </div>
          <div class="ai-assistant-message__bubble">{{ 'AI_ASSISTANT.GREETING' | translate }}</div>
        </article>
      }

      @for (turn of chat.turns(); track turn.id) {
        @for (step of turn.steps; track step.stepIndex) {
          @switch (step.kind) {
            @case ('user_message') {
              <article class="ai-assistant-message ai-assistant-message--user">
                <div class="ai-assistant-message__bubble">{{ step.text }}</div>
              </article>
            }
            @case ('assistant_text') {
              <article class="ai-assistant-message">
                <div
                  class="ai-assistant-message__avatar"
                  [class.ai-assistant-message__avatar--streaming]="
                    step.streaming && !chat.pendingConfirmation()
                  "
                  aria-hidden="true">
                  <i class="pi pi-sparkles"></i>
                </div>
                @if (step.streaming && !step.text) {
                  <div class="ai-assistant-message__bubble ai-assistant-message__bubble--pending">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                } @else {
                  <div class="ai-assistant-message__bubble ai-assistant-message__bubble--markdown">
                    <div class="ai-assistant-message__markdown" [innerHTML]="renderMarkdown(step.text)"></div>
                  </div>
                }
              </article>
            }
            @case ('tool_call') {
              <div
                class="ai-assistant-tool"
                [attr.aria-label]="'AI_ASSISTANT.TOOL.ACTIVITY' | translate">
                <span class="ai-assistant-tool__icon" aria-hidden="true">
                  <i class="pi {{ toolStatusIcon(step.status) }}"></i>
                </span>
                <span class="ai-assistant-tool__name">{{ step.toolName }}</span>
                <span class="ai-assistant-tool__status">
                  {{ toolStatusLabelKey(step.status) | translate }}
                </span>
              </div>
            }
            @case ('error') {
              <div class="ai-assistant-error" role="alert">{{ step.text }}</div>
            }
          }
        }
      }

      @if (chat.isThinking()) {
        <article class="ai-assistant-message">
          <div
            class="ai-assistant-message__avatar ai-assistant-message__avatar--streaming"
            aria-hidden="true">
            <i class="pi pi-sparkles"></i>
          </div>
          <div class="ai-assistant-message__bubble ai-assistant-message__bubble--pending">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </article>
      }

      @if (chat.pendingConfirmation(); as pendingConfirmation) {
        <section class="ai-assistant-confirmation" role="group">
          <div class="ai-assistant-confirmation__header">
            <i class="pi pi-shield" aria-hidden="true"></i>
            <span>{{ 'AI_ASSISTANT.CONFIRMATION.TITLE' | translate }}</span>
          </div>
          <div class="ai-assistant-confirmation__summary">
            {{ pendingConfirmation.displaySummary }}
          </div>
          @if (pendingConfirmationItems().length > 0) {
            <div class="ai-assistant-confirmation__items">
              @for (item of pendingConfirmationItems(); track item.callId) {
                <div class="ai-assistant-confirmation__item">
                  <div class="ai-assistant-confirmation__item-title">
                    {{ item.toolName }}
                  </div>
                  @if (item.args.length > 0) {
                    <dl class="ai-assistant-confirmation__args">
                      @for (arg of item.args; track arg.key) {
                        <div>
                          <dt>{{ arg.key }}</dt>
                          <dd>{{ arg.value }}</dd>
                        </div>
                      }
                    </dl>
                  }
                </div>
              }
            </div>
          }
          <div class="ai-assistant-confirmation__actions">
            <button
              pButton
              type="button"
              size="small"
              icon="pi pi-check"
              [label]="'AI_ASSISTANT.CONFIRMATION.APPROVE' | translate"
              [disabled]="chat.isResolvingConfirmation()"
              (click)="confirmPendingTool()"></button>
            <button
              pButton
              type="button"
              size="small"
              severity="secondary"
              icon="pi pi-times"
              [label]="'AI_ASSISTANT.CONFIRMATION.DECLINE' | translate"
              [disabled]="chat.isResolvingConfirmation()"
              (click)="cancelPendingTool()"></button>
          </div>
        </section>
      }

      @if (showSuggestions()) {
        <div class="ai-assistant-suggestions">
          <div class="ai-assistant-suggestions__label">
            {{ 'AI_ASSISTANT.TRY_ASKING' | translate }}
          </div>
          @for (suggestion of suggestions; track suggestion.labelKey) {
            <button
              type="button"
              class="ai-assistant-suggestion"
              (click)="sendSuggestion(suggestion.labelKey)">
              <i class="pi {{ suggestion.icon }}" aria-hidden="true"></i>
              <span>{{ suggestion.labelKey | translate }}</span>
            </button>
          }
        </div>
      }
    </div>
  }
</div>

<footer class="ai-assistant-composer">
  @if (chat.quotaExceeded(); as quota) {
    <div class="ai-assistant-error" role="alert">
      {{
        'AI_ASSISTANT.QUOTA_EXCEEDED'
          | translate: { used: quota.usedTokens, limit: quota.limitTokens }
      }}
    </div>
  }
  @if (chat.sendFailed()) {
    <div class="ai-assistant-error" role="alert">
      {{ errorTranslationKey() | translate }}
    </div>
  }
  <div class="ai-assistant-composer__row">
    <textarea
      #composerTextarea
      pTextarea
      [autoResize]="false"
      rows="1"
      [ngModel]="draft()"
      (ngModelChange)="onDraftChange($event)"
      (keydown)="onComposerKeydown($event)"
      [placeholder]="'AI_ASSISTANT.PLACEHOLDER' | translate"
      [attr.aria-label]="'AI_ASSISTANT.PLACEHOLDER' | translate"></textarea>
    @if (chat.canStop()) {
      <button
        pButton
        class="ai-assistant-composer__send"
        type="button"
        icon="pi pi-stop-circle"
        severity="secondary"
        [rounded]="true"
        [pTooltip]="'AI_ASSISTANT.STOP' | translate"
        [attr.aria-label]="'AI_ASSISTANT.STOP' | translate"
        [attr.title]="'AI_ASSISTANT.STOP' | translate"
        (click)="onStop()"></button>
    } @else {
      <button
        pButton
        class="ai-assistant-composer__send"
        type="button"
        icon="pi pi-arrow-up"
        [rounded]="true"
        [disabled]="!canSend()"
        [pTooltip]="'AI_ASSISTANT.SEND' | translate"
        [attr.aria-label]="'AI_ASSISTANT.SEND' | translate"
        [attr.title]="'AI_ASSISTANT.SEND' | translate"
        (click)="sendMessage()"></button>
    }
  </div>
  <div class="ai-assistant-composer__foot">
    <i class="pi pi-info-circle" aria-hidden="true"></i>
    <span>{{ 'AI_ASSISTANT.DISCLAIMER' | translate }}</span>
  </div>
</footer>
